import { useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import {
  FontAwesomeIcon,
  IconButton,
  Stack,
  Text,
  VoloPassBadgeIcon,
} from '@rivallapp/volosports-components';
import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons/faArrowUpFromBracket';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons/faCircleInfo';
import classNames from 'classnames/bind';

import styles from './styles.module.scss';
import { siteUrl } from '../../../../../environment';
import { useAlertMessage } from '../../../../../hooks';
import { LeagueProps, GameProps } from '../../../../../types';
import { Popup } from '../../../../../components';
import Button from '../../../../../components/Button';
import { ProgramTypeEnum } from '../../../../../shared/program-type-enum';
import { ProgramDetailItems } from '../../SharedComponents';

const cx = classNames.bind(styles);

const BULLET = '\u2022';

const ProgramDetailsCard = props => {
  const { league, game } = props;
  const { _id: leagueId, deepLink } = league || {};

  const isVoloPassExclusive = game
    ? game.league?.is_volo_pass_exclusive
    : league?.is_volo_pass_exclusive;

  const { showSuccess } = useAlertMessage();

  const shareUrl = game?._id
    ? `${siteUrl}/game/${game?._id}`
    : deepLink || `${siteUrl}/d/${leagueId}`;
  const linkType = 'Invite link';

  const [didCopy, setDidCopy] = useState(false);
  const [programInfo, setProgramInfo] = useState(false);

  const headerImg = !game?._id ? league?.photoUrl : game?.league?.photoUrl;
  const displayName = !game?._id ? league?.displayName : `${game?.league?.displayName} - Drop-in`;

  return (
    <>
      <Popup
        hidden={!programInfo}
        close={() => setProgramInfo(false)}
        className={cx('programPopup')}
      >
        <div>
          {league?.programType === ProgramTypeEnum.PICKUP ? (
            <div className="p-2">
              <h3 className="row p-2">What&apos;s a pickup?</h3>
              <p className="p-2">
                A pickup is a 45-90 min series of exhibition matches. Teams and formats are assigned
                upon arrival. These teams are temporary and last only for the duration of the game.
              </p>
            </div>
          ) : null}
          {game?._id ? (
            <div>
              <Stack space="2" textAlign="start">
                <Text fontWeight="semibold" fontSize="lg">
                  What&apos;s a Drop-In?
                </Text>
                <Text color="text.500">
                  A drop-in is a player who participates as a substitute for team members who are
                  needed for a game. Drop-in players help teams avoid forfeit.
                </Text>
                <Text marginLeft="2">
                  {BULLET} Playing Time Expectations:{' '}
                  <Text color="text.500">
                    For league drop-ins expect to play about half of the game. However, playing time
                    can always vary.
                  </Text>
                </Text>
                <Text marginLeft="2">
                  {BULLET} Play With Friends:{' '}
                  <Text color="text.500">
                    Sign up to play with your friends on the same team by searching for teams with
                    multiple drop-in spots available.
                  </Text>
                </Text>
                <Text marginLeft="2">
                  {BULLET} Competition Level:{' '}
                  <Text color="text.500">
                    Only drop-in to a competitive league if you know your skill set and are
                    comfortable playing at a high level.
                  </Text>
                </Text>
                <Text>
                  Pro-tips:{' '}
                  <Text color="text.500">
                    Be early!! Check if there are any notes! Use Chat to communicate with your
                    team!!
                  </Text>
                </Text>
              </Stack>
            </div>
          ) : null}
        </div>
      </Popup>
      <div className={cx('container')}>
        <div className={cx('programContainer')}>
          <div className={cx('detailsBackground', 'col-md-6')}>
            <div className={cx('d-flex', 'align-items-end', 'col-md-11 pl-0 p-2')}>
              <div className={cx('displayName')}>
                {displayName}{' '}
                {game?._id && (
                  <IconButton
                    colorScheme="white"
                    variant="link"
                    padding="1"
                    display="inline" // This works because this code only runs in a web environment
                    size="sm"
                    heigh="sm"
                    icon={<FontAwesomeIcon icon={faCircleInfo} />}
                    onPress={() => setProgramInfo(true)}
                  />
                )}
              </div>
              {isVoloPassExclusive && <VoloPassBadgeIcon colorScheme="white" ml={2} />}
              {!game && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() =>
                    window.open(`/l/${game?._id ? game?.league?._id : league?._id}`, '_blank')
                  }
                  onKeyDown={() =>
                    window.open(`/l/${game?._id ? game?.league?._id : league?._id}`, '_blank')
                  }
                  className={cx('programInfo', 'pl-2')}
                >
                  info
                </div>
              )}
            </div>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setProgramInfo(true)}
              onKeyDown={() => setProgramInfo(true)}
              className={cx('programInfo', 'pl-2')}
            >
              {league?.programType === ProgramTypeEnum.PICKUP ? <>What&apos;s a pickup?</> : null}
              {game?._id ? <>What&apos;s a drop-in?</> : null}
            </div>
            <ProgramDetailItems game={game} league={league} />
          </div>
          <div className={cx('headerImageContainer', 'col-md-6 d-md-block p-0')}>
            <img src={headerImg} alt="volo program" />
            <CopyToClipboard
              text={shareUrl}
              onCopy={() => {
                setDidCopy(true);
                showSuccess(`${linkType} copied!`);
              }}
            >
              <div className={cx('share')} data-for="c2c" data-tip={didCopy}>
                <Button className={cx('shareButton')} style={{ opacity: didCopy ? 0.7 : 1 }}>
                  {/* colorScheme was causing errors */}
                  <FontAwesomeIcon icon={faArrowUpFromBracket} />
                </Button>
              </div>
            </CopyToClipboard>
          </div>
        </div>
      </div>
    </>
  );
};

ProgramDetailsCard.propTypes = {
  league: LeagueProps,
  game: GameProps,
};

ProgramDetailsCard.defaultProps = {
  league: null,
  game: null,
};

export default ProgramDetailsCard;
